/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { ExpandCaret } from '@/Client/Icons';
import { COLORS, FONT } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { useProgressAlt } from './progressAltContext';
import { HEADER_PADDING_TOP_PX } from './progressAltMetrics';
import { getColumnKey, type ProgressAltColumn } from './progressAltModel';

/**
 * Holds the header box a little clear of the tab bar. The breathing room lives inside the sticky
 * cell rather than as canvas padding, so it travels with the header instead of leaving a
 * transparent strip for content to scroll through.
 */
const CELL_STYLE = css`
  label: PROGRESS_ALT_HEADER_CELL_STYLE;
  position: sticky;
  top: 0;
  z-index: 1;
  min-width: 0;
  display: flex;
  padding-top: ${HEADER_PADDING_TOP_PX}px;
  background: ${COLORS.appBg};
`;

/**
 * The bar is a plain part of its column: box, border and background span the column's natural width
 * and scroll with its photos. Only the words ride — see CONTENT_STYLE.
 */
const BAR_STYLE = css`
  label: PROGRESS_ALT_HEADER_STYLE;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid ${COLORS.stroke};
  border-radius: 0;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.midtone};
    background: ${COLORS.surfaceMuted};
  }
`;

const BAR_SELECTED_STYLE = css`
  border-color: ${COLORS.ink};
  background: ${COLORS.wash};
`;

/**
 * The bar's full width, so an expanded column's sticky title can ride anywhere along it. Relative,
 * so the caret can post over the bar's far end without entering the title's layout.
 */
const CONTENT_STYLE = css`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 6px 4px 9px;
  text-align: center;
`;

const TEXT_STYLE = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: center;
`;

/**
 * Only an expanded column's title rides the scroll while its bar stays put: parked one grid gap of
 * air off the date column while its own column is under the cursor, and shoved along on the way out
 * only once a single column of its bar remains — its width is capped to make exactly that so (see
 * `getHeaderTitleMaxWidth`). Where it parks moves with the date column's collapse, so `left` comes
 * in as a style. A collapsed column's bar is one column wide with nowhere to ride, so it carries
 * none of this — its title is plain layout.
 *
 * A wide column also has room to spare, so its title reads better ranged left than floating centred.
 */
const TEXT_EXPANDED_STYLE = css`
  position: sticky;
  align-items: flex-start;
  text-align: left;
`;

const TITLE_STYLE = css`
  font-family: ${FONT.display};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${pxToRem(12)};
  font-weight: 700;
`;

const SUBTITLE_STYLE = css`
  font-family: ${FONT.mono};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

/**
 * Rides along inside the bar rather than standing as a control of its own — the whole bar opens the
 * column now, so a bordered button here would offer a second way to do the one thing the bar does.
 * Posted absolutely over the bar's end so its presence never nudges the title's layout.
 */
const CARET_STYLE = css`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/** Names one column and carries its select and expand affordances. */
export const ProgressAltHeader = ({
  column,
  selected,
  expanded,
  canExpand,
  gridLeftPx,
  titleMaxWidthPx,
}: {
  column: ProgressAltColumn;
  selected: boolean;
  expanded: boolean;
  /** Only a column with a multi-photo cell has anything to open up. */
  canExpand: boolean;
  /** Where the columns begin, which is where an expanded column's title parks once it goes sticky. */
  gridLeftPx: number;
  /** Caps an expanded title so it is shoved off its park exactly when one column of its bar remains. */
  titleMaxWidthPx: number;
}) => {
  const { onSelectGroup, onSelectTask, toggleExpandedColumn } = useProgressAlt();
  // Selecting a task and selecting a group are the same gesture on the same bar; which one it is
  // depends on the column, and the workspace around the panel decides whether either is on offer.
  const selectScope = column.taskId
    ? onSelectTask && (() => onSelectTask(column.taskId!))
    : onSelectGroup && (() => onSelectGroup(column.groupId));

  /**
   * One gesture, both of the things a reader wants from a column heading: the route follows the column,
   * which brings it on screen, and the column opens out or shuts. They belong together — opening a
   * column is how you read it, and reading it is why you went to it.
   *
   * How far open it is rides on `aria-expanded` rather than in the label, so the bar keeps naming the
   * one thing it is: the way to this column.
   */
  const onActivate = selectScope || canExpand
    ? () => {
      selectScope?.();
      if (canExpand) toggleExpandedColumn(getColumnKey(column));
    }
    : undefined;

  return (
    <div css={CELL_STYLE}>
      <div
        css={[BAR_STYLE, selected && BAR_SELECTED_STYLE]}
        data-progress-alt-column={column.taskId ?? column.groupId}
        data-progress-alt-expanded={expanded ? 'true' : undefined}
        role={onActivate ? 'button' : undefined}
        aria-label={onActivate ? `Select ${column.title}` : undefined}
        aria-expanded={canExpand ? expanded : undefined}
        onClick={onActivate}
      >
        <span css={CONTENT_STYLE}>
          <span
            css={[TEXT_STYLE, expanded && TEXT_EXPANDED_STYLE]}
            style={expanded ? { left: gridLeftPx, maxWidth: titleMaxWidthPx } : undefined}
          >
            <span css={TITLE_STYLE}>{column.title}</span>
            <span css={SUBTITLE_STYLE}>{column.subtitle}</span>
          </span>
          {canExpand
            ? (
              <span css={CARET_STYLE} aria-hidden>
                {/* The caret's own glyph points right, so `expanded` alone flips it to left. */}
                <ExpandCaret expanded={expanded} color={COLORS.midtone} />
              </span>
            )
            : null}
        </span>
      </div>
    </div>
  );
};
